import { createFileRoute, Outlet } from "@tanstack/react-router";
import { trpc, trpcUtils } from "@/hooks/trpc";
import { TestTemplateId } from "@brewbase/db/types";
import { z } from "zod";
import { useSidebarConfig } from "@/hooks/use-sidebar-config";
import { FlaskConical } from "lucide-react";
import { useState } from "react";
import { TestTemplateSidebarHeader } from "@/features/test-template/sidebar-header";
import { TestTemplateRevisionHistoryDrawer } from "@/features/test-template/revision-history-drawer";
import { RequireCapability } from "@/components/require-capability";
import { useMethodCurves } from "@/features/calibration-curves/use-method-curves";

export const Route = createFileRoute(
  "/analytics/templates/$template_id",
)({
  component: () => (
    <RequireCapability cap="test-template:edit">
      <RouteComponent />
    </RequireCapability>
  ),
  params: z.object({
    template_id: z.coerce
      .number()
      .transform((val) => val as TestTemplateId),
  }),
  loader: async ({ params }) => {
    await trpcUtils.tests.testTemplates.getById.ensureData(params.template_id);
  },
});

function RouteComponent() {
  const { template_id } = Route.useParams();
  const [historyOpen, setHistoryOpen] = useState(false);

  const { data: template, isLoading } =
    trpc.tests.testTemplates.getById.useQuery(template_id);
  const curves = useMethodCurves(template_id);

  useSidebarConfig({
    layer: 2,
    parent: {
      label: "Test Templates",
      icon: FlaskConical,
      href: "/analytics/templates",
    },
    customHeader: template ? (
      <TestTemplateSidebarHeader
        template={template}
        onOpenHistory={() => setHistoryOpen(true)}
      />
    ) : null,
    navigation: [
      {
        type: "link",
        id: "general",
        label: "General",
        href: `/analytics/templates/${template_id}`,
        exact: true,
      },
      {
        type: "link",
        id: "attributes",
        label: "Attributes",
        href: `/analytics/templates/${template_id}/attributes`,
      },
      // Calibration curves: a method's compounds, or any template's once it has one.
      ...(template?.blueprint || curves.length > 0
        ? [
            {
              type: "link" as const,
              id: "curves",
              label: "Curves",
              href: `/analytics/templates/${template_id}/curves`,
            },
          ]
        : []),
      ...(template?.blueprint
        ? [
            {
              type: "link" as const,
              id: "blueprint",
              label: "Blueprint",
              href: `/analytics/templates/${template_id}/blueprint`,
            },
          ]
        : []),
    ],
  });

  if (isLoading || !template) {
    return (
      <div className="flex items-center justify-center h-full">
        Loading template...
      </div>
    );
  }

  return (
    <div className="h-full">
      <Outlet />
      <TestTemplateRevisionHistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        testTemplateId={template.id}
      />
    </div>
  );
}
